<!--
  文件树组件
  功能：
  1. 显示文件树结构
  2. 支持多语言切换（中文/英文）
  3. 支持文件下载
  4. 支持文件预览
  5. 支持键盘导航
  6. 支持文件选择和时间范围显示
-->
<template>
  <div class="tree-box" role="tree" aria-label="SmartPNT Data Tree">
    
    <!-- 头部区域 -->
    <div class="header">
      
      <!-- 语言选择和文档下载区域 -->
      <div class="language-box">
        <div class="language">
          <a-select 
            v-model="language" 
            @change="handleChange" 
            size="small" 
            style="width: 80px;"
            aria-label="Select language"
          >
            <a-select-option value="en">English</a-select-option>
            <a-select-option value="zh">中文</a-select-option>
          </a-select>
        </div>
        <a 
          href="http://smartpnt-datasets.com/operation.pdf" 
          target="_blank"
          rel="noopener noreferrer"
          :aria-label="$t('downloadDocument')"
          class="download-link"
        >
          <svg class="download-icon" viewBox="0 0 24 24" width="14" height="14">
            <path fill="currentColor" d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/>
          </svg>
          {{$t('downloadDocument')}}
        </a>
      </div>
      
      <!-- 时间显示和操作按钮区域 -->
      <div class="btn-row">
        <div class="time-row">
          <div class="time">
            <div class="label">{{$t('start')}}: {{ startTime }}</div>
          </div>
          <div class="time">
            <div class="label">{{$t('end')}}: {{ endTime }}</div>
          </div>
        </div>
        <div class="btns">
          <button 
            @click="download"
            class="btn-action"
            :aria-label="$t('download')"
          >
            <img src="@/assets/btn-download.png" alt="download icon">{{$t('download')}}
          </button>
          <button 
            @click="reset"
            class="btn-action"
            :aria-label="$t('reset')"
          >
            <img src="@/assets/btn-reset.png" alt="reset icon">{{$t('reset')}}
          </button>
        </div>
      </div>
    </div>
    
    <!-- 文件树区域 -->
    <div class="tree-outer">
      <div class="title">SmartPNT</div>
      <a-tree
        ref="tree"
        class="tree"
        :showLine="true"
        v-model="checkedKeys"
        :checkable="true"
        :expandedKeys="expandedKeys"
        :tree-data="treeData"
        :replaceFields="defaultProps"
        @expand="handleExpand"
        @select="selectNode"
        @rightClick="rightClick"
        @check="handleCheck"
        :selectable="false"
        role="tree"
        aria-label="Data tree"
      >
        <template slot="title" slot-scope="item">
          <a-tooltip 
            placement="top" 
            :title="tooltipVisible(item)"
            :mouseEnterDelay="0.5"
          >
            <template>{{ item.name }}</template>
          </a-tooltip>
        </template>
      </a-tree>
    </div>
    
  </div>
</template>

<script>
import {getDownloadUrl, getFileTree, mapMark} from "@/request/api";

export default {
  name: 'tree',
  components: {},
  props: ['start', 'end'], // 接收起点和终点数据
  data() {
    return {
      checkedKeys: [], // 选中的节点键值
      expandedKeys: [], // 展开的节点键值
      defaultProps: {
        children: 'children', // 子节点字段名
        title: 'name', // 节点标题字段名
        key: 'key', // 节点键值字段名
      },
      treeData: [], // 树形数据

      language: localStorage.getItem("locale") ||"en", // 当前语言，默认英文

      leafNodes: [], // 选中的叶子节点
    }
  },
  mounted() {
    this.getDataList() // 初始化加载树数据
    // 添加键盘导航支持
    document.addEventListener('keydown', this.handleKeyNavigation);
  },
  beforeDestroy() {
    // 移除键盘导航事件监听
    document.removeEventListener('keydown', this.handleKeyNavigation);
  },
  computed: {
    /**
     * 格式化起点时间
     * @returns {String} 格式化后的时间字符串
     */
    startTime() {
      if(this.start) {
        return this.formatTime(this.parseDate(this.start.data))
      }
      return ''
    },
    /**
     * 格式化终点时间
     * @returns {String} 格式化后的时间字符串
     */
    endTime() {
      if(this.end) {
        return this.formatTime(this.parseDate(this.end.data))
      }
      return ''
    }
  },
  methods: {
    /**
     * 判断是否显示工具提示
     * @param {Object} data - 节点数据
     * @returns {String|undefined} 工具提示文本
     */
    tooltipVisible(data){
      if(data.name.includes("jpg") || data.name.includes("png")){
        return this.$t('rightClickPreview')
      }
    },
    /**
     * 处理节点选中事件
     * @param {Array} checkedKeys - 选中的节点键值数组
     * @param {Object} e - 事件对象
     */
    handleCheck(checkedKeys, { checkedNodes }) {
      let leafNodes = []
      checkedNodes.forEach(node => {
        if(node.componentOptions.children.length === 0){
          leafNodes.push(node.key)
        }
      });
      this.leafNodes = leafNodes;
    },
    /**
     * 处理语言切换
     */
    handleChange(){
      if (this.language === "zh"){
        localStorage.setItem("locale", "zh");
        this.$i18n.locale = localStorage.getItem("locale");
      } else if (this.language === "en"){
        localStorage.setItem("locale", "en");
        this.$i18n.locale = localStorage.getItem("locale");
      }
    },
    /**
     * 加载树形数据
     */
    getDataList() {
      getFileTree().then(res => {
        this.treeData = res.data[0].children;
        this.treeData.forEach(it => {
          it.key = it.key + '_menu'
          it.checkable = false;
          if(!it.name.includes("pytools") && !it.name.includes("config")){
            it.children.forEach(c => {
              c.key = c.key + '_project'
              c.selectable = true;
              c.class = "tree-link"
              c.checkable = false;
            })
          }
        })
      })
    },
    /**
     * 处理节点展开事件
     * @param {Array} expandedKeys - 展开的节点键值数组
     * @param {Object} e - 事件对象
     */
    handleExpand(expandedKeys, {node}) {
      // 判断是展开还是折叠操作
      const isExpanding = expandedKeys.includes(node.eventKey) && !this.expandedKeys.includes(node.eventKey);
      
      // 如果是展开操作，实现手风琴效果
      if (isExpanding && expandedKeys[expandedKeys.length - 1].includes("_project")) {
        // 找到同级别节点并折叠它们
        const filteredKeys = this.getSiblingFilteredKeys(node.eventKey, expandedKeys);
        this.expandedKeys = filteredKeys;
        
        const nodeKey = [node.eventKey];
        mapMark(nodeKey).then(res => {
          this.$emit('treeSelected', res.data, nodeKey)
        })
      } else {
        // 折叠操作，正常更新
        this.expandedKeys = expandedKeys;
      }
    },

    /**
     * 获取过滤后的展开键（实现手风琴效果）
     * @param {String} currentKey - 当前展开的节点键值
     * @param {Array} newExpandedKeys - 新的展开键值数组
     * @returns {Array} 过滤后的展开键值数组
     */
    getSiblingFilteredKeys(currentKey, newExpandedKeys) {
      // 找到当前节点的同级节点
      const siblingKeys = this.findSiblingKeys(currentKey);
      
      // 从展开键中移除同级节点，但保留当前节点
      const filteredKeys = newExpandedKeys.filter(key => {
        return !siblingKeys.includes(key) || key === currentKey;
      });
      
      return filteredKeys;
    },

    /**
     * 查找同级节点的键值
     * @param {String} nodeKey - 节点键值
     * @returns {Array} 同级节点键值数组
     */
    findSiblingKeys(nodeKey) {
      const siblingKeys = [];
      
      // 递归查找同级节点
      const findSiblings = (nodes, targetKey, parentKey = null) => {
        for (let node of nodes) {
          if (node.key === targetKey) {
            // 找到目标节点，返回其同级节点
            return nodes.map(n => n.key);
          }
          if (node.children && node.children.length > 0) {
            const result = findSiblings(node.children, targetKey, node.key);
            if (result) {
              return result;
            }
          }
        }
        return null;
      };
      
      const result = findSiblings(this.treeData, nodeKey);
      return result || [];
    },
    /**
     * 处理节点选择事件
     * @param {Array} selectedKeys - 选中的节点键值数组
     * @param {Object} e - 事件对象
     */
    selectNode(selectedKeys, {selected}) {
      if (selected && selectedKeys.length > 0) {
        const nodeKey = selectedKeys[0];
        
        // 展开选中的节点，并应用手风琴效果
        const siblingKeys = this.findSiblingKeys(nodeKey);
        const filteredKeys = this.expandedKeys.filter(key => !siblingKeys.includes(key));
        this.expandedKeys = [...filteredKeys, nodeKey];
        
        // 展现轨迹
        mapMark(selectedKeys).then(res => {
          this.$emit('treeSelected', res.data, selectedKeys)
        })
      }
    },
    /**
     * 处理右键点击事件
     * @param {Object} e - 事件对象
     */
    rightClick({event, node}){
      const name = node.dataRef.name;
      if (name.includes(".jpg") || name.includes(".png")) {
        //预览图片
        const arr = [node.eventKey];
        getDownloadUrl({
          ids: arr.join(','),
          start: this.start,
          end: this.end
        }).then(res => {
          const list = res.data;
          list.forEach(item => {
            window.open(item.url, '_blank');
          })
        })
      }
    },
    /**
     * 重置树状态
     */
    reset(){
      // 清空所有状态
      this.checkedKeys = []
      this.leafNodes = []
      this.expandedKeys = []
      
      // 清除树组件的选中状态
      this.$refs.tree && this.$refs.tree.setSelectedKeys([])
      
      this.$emit('reset')
    },
    /**
     * 清除树状态
     */
    clear(){
      this.checkedKeys = []
      this.leafNodes = []
      this.expandedKeys = []
    },
    /**
     * 触发下载事件
     */
    download() {
      this.$emit("download", this.leafNodes)
    },
    /**
     * 过滤选中的节点
     * @param {Array} data - 节点键值数组
     */
    filterSelect(data){
      this.checkedKeys = data
    },
    /**
     * 设置选中的节点
     * @param {Array} data - 节点键值数组
     */
    setSelect(data){
      this.checkedKeys = data;
      this.leafNodes = data;
    },
    /**
     * 添加选中的节点
     * @param {Array} dataArray - 节点键值数组
     */
    addSelectNode(dataArray){
      // 使用 Vue 的响应式更新方式
      this.$set(this, 'checkedKeys', [...this.checkedKeys, ...dataArray]);
      this.$set(this, 'leafNodes', [...this.leafNodes, ...dataArray]);
      
      // 确保节点展开
      const newExpandedKeys = [...this.expandedKeys];
      dataArray.forEach(key => {
        if (!newExpandedKeys.includes(key)) {
          newExpandedKeys.push(key);
        }
      });
      this.$set(this, 'expandedKeys', newExpandedKeys);
    },
    /**
     * 解析日期字符串
     * @param {String} timeStr - 日期字符串
     * @returns {Date} 解析后的日期对象
     */
    parseDate(timeStr) {
      const parts = timeStr.split(/[- :]/);
      return new Date(parts[0], parts[1] - 1, parts[2], parts[3], parts[4], parts[5]);
    },
    /**
     * 格式化日期对象
     * @param {Date} date - 日期对象
     * @returns {String} 格式化后的日期字符串
     */
    formatTime(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hour = String(date.getHours()).padStart(2, '0');
      const minute = String(date.getMinutes()).padStart(2, '0');
      const second = String(date.getSeconds()).padStart(2, '0');

      return `${year}/${month}/${day} ${hour}.${minute}.${second}`;
    },

    /**
     * 处理键盘导航
     * @param {KeyboardEvent} event - 键盘事件对象
     */
    handleKeyNavigation(event) {
      const { key } = event;
      const activeElement = document.activeElement;
      
      if (key === 'ArrowDown' || key === 'ArrowUp') {
        event.preventDefault();
        // 实现上下导航逻辑
        this.navigateTree(key === 'ArrowDown' ? 'next' : 'previous');
      } else if (key === 'Enter' && activeElement.classList.contains('btn-action')) {
        // 处理按钮的键盘操作
        if (activeElement.getAttribute('aria-label') === this.$t('download')) {
          this.download();
        } else if (activeElement.getAttribute('aria-label') === this.$t('reset')) {
          this.reset();
        }
      }
    },

    /**
     * 树节点导航
     * @param {String} direction - 导航方向，'next'或'previous'
     */
    navigateTree(direction) {
      // 实现树节点的键盘导航
      const nodes = this.$el.querySelectorAll('.ant-tree-node-content-wrapper');
      const currentIndex = Array.from(nodes).indexOf(document.activeElement);
      
      if (direction === 'next' && currentIndex < nodes.length - 1) {
        nodes[currentIndex + 1].focus();
      } else if (direction === 'previous' && currentIndex > 0) {
        nodes[currentIndex - 1].focus();
      }
    },

    /**
     * 优化树节点渲染性能
     * @param {Object} node - 节点数据
     * @returns {Boolean} 是否需要更新节点
     */
    shouldNodeUpdate(node) {
      return this.expandedKeys.includes(node.key) || this.checkedKeys.includes(node.key);
    }
  },
}
</script>

<style scoped lang="scss">
.tree-box {
  position: fixed;
  left: 20px;
  top: 5%;
  width: 490px;
  height: 90%;
  background: rgba(0, 0, 0, 0.6);
  z-index: 99;
  border-radius: 6px;
  color: #fff;
  border: 1px solid #fff;
  display: flex;
  flex-direction: column;

  /* 响应式布局 */
  @media screen and (max-width: 768px) {
    position: fixed;
    left: 0;
    top: 0;
    width: 100%;
    height: 100vh;
    border-radius: 0;
  }

  .header {
    background: rgba(0, 0, 0, 0.1);
    border-radius: 6px 6px 0 0;
    padding: 10px 10px 0;
    flex-shrink: 0;

    .language-box {
      display: flex;
      align-items: center;
      justify-content: space-between;
      font-size: 14px;
      margin-bottom: 10px;
      padding: 5px 0;

      .language {
        margin-right: 10px;

        ::v-deep .ant-select {
          .ant-select-selector {
            background: transparent;
            border: 1px solid rgba(255, 255, 255, 0.5);
            color: #fff;
            transition: all 0.3s ease;

            &:hover {
              border-color: #fff;
            }

            .ant-select-selection-item {
              color: #fff;
            }
          }

          .ant-select-arrow {
            color: rgba(255, 255, 255, 0.5);
          }

          &.ant-select-focused {
            .ant-select-selector {
              border-color: #1890ff;
              box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
            }
          }
        }
      }

      .download-link {
        color: #fff;
        text-decoration: none;
        padding: 4px 8px;
        border-radius: 4px;
        transition: all 0.3s ease;
        display: flex;
        align-items: center;
        gap: 6px;

        .download-icon {
          opacity: 0.8;
          transition: opacity 0.3s ease;
        }

        &:hover {
          background: rgba(255, 255, 255, 0.1);
          
          .download-icon {
            opacity: 1;
          }
        }

        &:focus {
          outline: 2px solid #1890ff;
          outline-offset: 2px;
        }
      }
    }

    .btn-row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-top: 10px;
      flex-wrap: wrap;
      gap: 10px;

      .time-row {
        display: flex;
        flex-direction: column;
        flex: 1;
        min-width: 200px;

        .time {
          display: flex;
          margin-bottom: 5px;

          .label {
            padding-right: 10px;
            font-weight: bold;
          }
        }
      }

      .btns {
        display: flex;
        gap: 10px;
        flex-wrap: wrap;

        .btn-action {
          background: transparent;
          border: 1px solid #FFFFFF;
          color: #FFFFFF;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0 11px;
          min-width: 92px;
          height: 32px;
          font-size: 12px;
          border-radius: 5px;
          transition: all 0.3s ease;

          &:focus {
            outline: 2px solid #1890ff;
            outline-offset: 2px;
          }

          &:hover {
            background: rgba(255, 255, 255, 0.1);
          }

          img {
            width: 13px;
            height: 13px;
            margin-right: 5px;
          }
        }
      }
    }
  }

  .tree-outer {
    flex: 1;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    padding: 20px;

    .title {
      font-weight: bold;
      font-size: 14px;
      color: #FFFFFF;
      margin-bottom: 10px;
    }

    .tree {
      flex: 1;
      overflow-y: auto;
      scrollbar-width: thin;
      scrollbar-color: rgba(255, 255, 255, 0.5) rgba(0, 0, 0, 0.1);

      /* 自定义滚动条样式 */
      &::-webkit-scrollbar {
        width: 8px;
      }

      &::-webkit-scrollbar-track {
        background: rgba(0, 0, 0, 0.1);
        border-radius: 4px;
      }

      &::-webkit-scrollbar-thumb {
        background: rgba(255, 255, 255, 0.5);
        border-radius: 4px;
      }

      &::-webkit-scrollbar-thumb:hover {
        background: rgba(255, 255, 255, 0.8);
      }

      ::v-deep {
        .ant-tree-node-content-wrapper {
          color: #fff;
          transition: all 0.3s ease;
        }

        span.ant-tree-switcher {
          color: #fff;
          background: transparent;
        }

        .ant-tree-node-content-wrapper.ant-tree-node-selected,
        .ant-tree-node-content-wrapper:hover {
          background-color: rgba(87, 87, 87, 0.5);
        }

        .ant-tree-treenode {
          padding: 4px 0;
        }
      }
    }
  }

  /* 链接样式 */
  ::v-deep .tree-link > .ant-tree-node-content-wrapper > .ant-tree-title {
    text-decoration: underline;
    font-weight: bold;
    color: #1890ff91;
    transition: color 0.3s ease;

    &:hover {
      color: #1890ff;
    }
  }
}

.btn-action {
  background: transparent;
  border: 1px solid #FFFFFF;
  color: #FFFFFF;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 11px;
  min-width: 92px;
  height: 32px;
  font-size: 12px;
  border-radius: 5px;
  transition: all 0.3s ease;

  &:focus {
    outline: 2px solid #1890ff;
    outline-offset: 2px;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }

  img {
    width: 13px;
    height: 13px;
    margin-right: 5px;
  }
}

/* 暗色主题支持 */
@media (prefers-color-scheme: dark) {
  .tree-box {
    background: rgba(0, 0, 0, 0.8);
    
    .header {
      background: rgba(0, 0, 0, 0.3);
    }

    .language-box {
      .language {
        ::v-deep .ant-select {
          .ant-select-selector {
            background: rgba(0, 0, 0, 0.3);
          }
        }
      }
    }
  }
}
</style>
